import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useState } from 'react'
import { Container } from '../components/Animations'
import { useParams, Link } from 'react-router-dom'
import Spinner from '../components/Spinner'
import Radom, { Customer, ISubscription } from '../state/Radom'
import BreadCrumbs from '../components/Breadcrumbs'
import { PaymentDetailsSection } from '../components/PaymentDetails'
import { StatusBadge } from './CheckoutSessions'
import { asSimpleDateTime, formatCurrency, successToast } from '../util/Util'
import ConfirmButtonModal from '../components/ConfirmButtonModal'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import Dots from '../icons/Dots'
import PaymentMethodDisplay from '../components/PaymentMethodDisplay'
import TimeText from '../components/TimeText'
import { genSectionItems } from '../util/SectionItems'
import { RADOM_COLORS } from '../util/Constants'
import { Section, SubTitle, SectionContent, SectionItem } from '../components/Shared'

const customerColumnCount = 3

// ---------------------------------------------------------------------------------------------------------------------
const Subscription = observer((): ReactElement => {
  const params = useParams()
  const [isLoading, setIsLoading] = useState(true)
  const [subscription, setSubscription] = useState<ISubscription>()
  const [customer, setCustomer] = useState<Customer>()

  const fetchSubscription = async (): Promise<void> => {
    setIsLoading(true)
    if (params.subscriptionId) {
      try {
        const s = await Radom.getSubscription(params.subscriptionId)
        setSubscription(s)
      } catch (err) {
        console.error('Failed to fetch subscription', err)
      } finally {
        setIsLoading(false)
      }
    }
  }

  useEffect(() => {
    fetchSubscription()
  }, [params.subscriptionId])

  // ---------------------------------------------------------------------------------------------------------------------
  const fetchCustomerByEmail = async (email: string | undefined): Promise<void> => {
    if (email) {
      setIsLoading(true)
      if (params.subscriptionId) {
        try {
          const cust = await Radom.getCustomerByEmail(email)
          setCustomer(cust)
        } catch (err) {
          console.error(`Failed to fetch customer ${err}`)
        } finally {
          setIsLoading(false)
        }
      }
    }
  }

  useEffect(() => {
    fetchCustomerByEmail(subscription?.subscriptionType.emailInvoiceSubscription?.emailAddress)
  }, [subscription])

  // ---------------------------------------------------------------------------------------------------------------------

  if (isLoading || !subscription) {
    return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 140, gap: 20 }}>
      <Spinner />
    </div>
  }

  // ---------------------------------------------------------------------------------------------------------------------
  return <Container style={{ flexDirection: 'column', fontSize: 14, gap: 30 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <BreadCrumbs items={[
        { item: 'Subscriptions', to: '/subscriptions' },
        { item: subscription.id, to: '.' }
      ]} />
      {subscription.status === 'active' && (
        <Dropdown
          noChevron
          selectedContent={<Dots width={15} />}
          dropdownContent={
            <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
              <ConfirmButtonModal
                title='Cancel subscription'
                description='Are you sure you want to cancel this subscription?'
                action='Cancel subscription'
                onConfirm={async () => {
                  await Radom.cancelSubscription(subscription.id)
                  successToast('Cancelled subscription')
                  fetchSubscription()
                }}
                ActionComponent={props => <DropdownItem {...props}>Cancel subscription</DropdownItem>}
              />
            </div>
          }
        />
      )}
    </div>

    {
      // ---------------------------------------------------------------------------------------------------------------
    }
    <Section>
      <SubTitle style={{ fontSize: 18 }}>Subscription details</SubTitle>
      <SectionContent numColumns={3}>
        <SectionItem>
          <SubTitle>ID</SubTitle>
          <div>{subscription.id}</div>
        </SectionItem>
        <SectionItem>
          <SubTitle>Subscription type</SubTitle>
          <span>{subscription.subscriptionType.automatedEVMSubscription ? 'Auto renew' : 'Email invoice'}</span>
        </SectionItem>
        <SectionItem>
          <SubTitle>Amount</SubTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <PaymentMethodDisplay
              method={{ network: subscription.network, token: subscription.token }}
              excludeName
              disableTooltip={false} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {formatCurrency(Number(subscription.amount), subscription.currency)}
              <span style={{ fontWeight: 300, opacity: 0.75 }}>{subscription.period}</span>
            </div>
          </div>
        </SectionItem>
        <SectionItem>
          <SubTitle>Status</SubTitle>
          <StatusBadge status={subscription.status}>{subscription.status}</StatusBadge>
        </SectionItem>
        <SectionItem>
          <SubTitle>Created</SubTitle>
          <TimeText date={new Date(subscription.createdAt)} />
        </SectionItem>
        {subscription.status === 'active' && (
          <SectionItem>
            <SubTitle>Next payment due</SubTitle>
            <TimeText date={new Date(subscription.nextBillingDateAt)} />
          </SectionItem>
        )}
        {subscription.cancelledAt && (
          <SectionItem>
            <SubTitle>Cancelled on</SubTitle>
            <TimeText date={new Date(subscription.cancelledAt)} />
          </SectionItem>
        )}
        {subscription.expiredAt && (
          <SectionItem>
            <SubTitle>Expired on</SubTitle>
            <TimeText date={new Date(subscription.expiredAt)} />
          </SectionItem>
        )}
      </SectionContent>
    </Section>

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Show customer details
    }
    <Section>
      <SubTitle style={{ fontSize: 18 }}>Customer details</SubTitle>
      {(subscription.subscriptionType.automatedEVMSubscription ||
      subscription.subscriptionType.emailInvoiceSubscription) &&
        !customer &&
        genSectionItems([
          { heading: 'EVM address', content: subscription.subscriptionType.automatedEVMSubscription?.buyerAddress },
          { heading: 'Email address', content: subscription.subscriptionType.automatedEVMSubscription?.emailAddress },
          { heading: 'Email address', content: subscription.subscriptionType.emailInvoiceSubscription?.emailAddress }
        ], customerColumnCount)}

      {customer &&
        <SectionContent numColumns={customerColumnCount}>
          {
            genSectionItems([
              {
                heading: 'Customer ID',
                content: (
                  <Link to={`/customer/${customer.id}`} style={{ color: RADOM_COLORS.BLACK }}>
                    {customer.id}
                  </Link>
                )
              },
              ...(customer.accountType === 'business'
                ? [
                  { heading: 'Company Name', content: customer.companyName },
                  { heading: 'Company Number', content: customer.companyNumber },
                  { heading: '', content: null }

                ]
                : []),
              { heading: 'Name', content: customer.name },
              { heading: 'Email address', content: customer.email },
              { heading: 'Date of birth', content: customer.dob },
              { heading: 'Billing address', content: customer.billingAddress },
              { heading: 'Phone number', content: customer.phone },
              { heading: 'Country', content: customer.residenceCountry },
              { heading: 'Telegram', content: customer.telegram },
              { heading: 'Discord', content: customer.discord },
              { heading: 'Created at', content: asSimpleDateTime(customer.createdAt) }
            ], customerColumnCount)
          }
        </SectionContent>
      }
    </Section>

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Show payment history
    }
    {(subscription.payments?.length ?? 0) > 0 && (
      <Section>
        <SubTitle style={{ fontSize: 18 }}>Payment history</SubTitle>
        <SectionContent numColumns={1}>
          {(subscription.payments ?? []).map((p, index) => {
            const txHash = p.transactionHashes?.[0]
            return (
              <PaymentDetailsSection
                key={index}
                payment={{
                  managed: {
                    network: p.network,
                    token: p.token ?? '',
                    amount: p.amount ?? '0',
                    networkFee: p.networkFee,
                    conversionRates: [],
                    paymentEventId: '',
                    transactions: [{
                      network: p.network,
                      token: p.token ?? '',
                      amount: p.amount ?? '0',
                      transactionHash: txHash,
                      blockTimestamp: p.date
                    }]
                  }
                }}
                netPayment={p.netAmount ? Number(p.netAmount) : Number(p.amount)}
                short
              />
            )
          })}
        </SectionContent>
      </Section>
    )}
  </Container>
})

export default Subscription
